import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import {
  generateServiceOrderQuotePDF,
  type SOEquipmentGroupForPDF,
  type SOQuoteItemForPDF,
  type ServiceOrderQuotePDFData,
} from '@/utils/pdfGenerator';
import { generatePdfFileName } from '@/utils/fileNameUtils';
import { captureException } from '@/lib/monitoring';
import { useAuth } from '@/contexts/AuthContext';
import { Download, Loader2 } from 'lucide-react';

interface ServiceOrderQuotePrintDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  serviceOrderId: string;
}

export const ServiceOrderQuotePrintDialog = ({
  open,
  onOpenChange,
  serviceOrderId,
}: ServiceOrderQuotePrintDialogProps) => {
  const { company: authCompany } = useAuth();
  const companyId = authCompany?.id;

  const [isLoading, setIsLoading]     = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [hasError, setHasError]       = useState(false);
  const [quotationNumber, setQuotationNumber] = useState<string | null>(null);
  const [customerName, setCustomerName] = useState<string | null>(null);
  const [pdfData, setPdfData] = useState<ServiceOrderQuotePDFData | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    if (open && serviceOrderId) {
      loadData();
    } else {
      setPdfData(null);
      setQuotationNumber(null);
      setCustomerName(null);
      setHasError(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, serviceOrderId]);

  const loadData = async () => {
    if (!companyId) {
      captureException(
        new Error('ServiceOrderQuotePrintDialog: companyId ausente'),
        { context: 'loadData' }
      );
      toast({
        title: 'Erro',
        description: 'Empresa não identificada.',
        variant: 'destructive',
      });
      return;
    }

    setIsLoading(true);
    setHasError(false);

    try {
      // 1. Carrega OS → quotation_id + equipamentos (ordenados por position)
      const { data: soData, error: soError } = await supabase
        .from('service_orders')
        .select(`
          id,
          quotation_id,
          service_order_equipment(id, equipment_name, serial_number, position)
        `)
        .eq('id', serviceOrderId)
        .eq('company_id', companyId)
        .single();

      if (soError) throw soError;

      if (!soData?.quotation_id) {
        setHasError(true);
        return;
      }

      // 2. Carrega orçamento + cliente + condição de pagamento
      const { data: quotationData, error: quotationError } = await supabase
        .from('quotations')
        .select(`
          *,
          customer:customers(
            id, name, document_number, email, phone,
            address, address_number, neighborhood, city, state
          ),
          payment_condition:payment_conditions!payment_condition_id(id, name)
        `)
        .eq('id', soData.quotation_id)
        .eq('company_id', companyId)
        .single();

      if (quotationError) throw quotationError;

      // 3. Carrega itens com embeds de produto e serviço
      const { data: itemsData, error: itemsError } = await supabase
        .from('quotation_items')
        .select(`
          *,
          product:products(name, sku),
          service:services(service_name)
        `)
        .eq('quotation_id', soData.quotation_id)
        .eq('company_id', companyId);

      if (itemsError) throw itemsError;

      // 4. Nome do vendedor
      let salespersonName: string | undefined;
      const sp = (quotationData as any).salesperson;
      if (sp?.full_name) {
        salespersonName = sp.full_name;
      } else if (quotationData.created_by) {
        const { data: profileData } = await supabase
          .from('profiles')
          .select('full_name')
          .eq('user_id', quotationData.created_by)
          .maybeSingle();
        if (profileData?.full_name) salespersonName = profileData.full_name;
      }

      // 5. Monta os grupos por equipamento
      const equipment = ((soData.service_order_equipment ?? []) as Array<{
        id: string;
        equipment_name: string;
        serial_number: string | null;
        position: number;
      }>).sort((a, b) => a.position - b.position);

      const items = (itemsData ?? []) as Array<SOQuoteItemForPDF & { service_order_equipment_id?: string | null }>;

      const groups: SOEquipmentGroupForPDF[] = equipment.map((eq) => {
        const label = [
          eq.equipment_name || 'Equipamento',
          eq.serial_number ? `S/N: ${eq.serial_number}` : null,
        ]
          .filter(Boolean)
          .join(' — ');
        const eqItems = items.filter(
          (it) => it.service_order_equipment_id === eq.id
        );
        const subtotal = eqItems.reduce(
          (acc, it) => acc + Number(it.total || 0),
          0
        );
        return {
          equipment_id:    eq.id,
          equipment_label: label,
          items:           eqItems,
          subtotal,
        };
      });

      // Itens órfãos (sem service_order_equipment_id ou equipamento não listado)
      const knownIds = new Set(equipment.map((eq) => eq.id));
      const orphans = items.filter(
        (it) =>
          !it.service_order_equipment_id ||
          !knownIds.has(it.service_order_equipment_id)
      );
      if (orphans.length > 0) {
        groups.push({
          equipment_id:    null,
          equipment_label: 'Sem equipamento',
          items:           orphans,
          subtotal: orphans.reduce((acc, it) => acc + Number(it.total || 0), 0),
        });
      }

      // 6. Normaliza campos do orçamento
      const paymentConditions =
        (quotationData as any).payment_condition?.name ??
        quotationData.payment_conditions ??
        null;

      const customer = (quotationData as any).customer ?? {};
      setQuotationNumber(quotationData.quotation_number);
      setCustomerName(customer.name ?? null);

      setPdfData({
        quotation: {
          quotation_number: quotationData.quotation_number,
          quotation_date:   quotationData.quotation_date,
          valid_until:      quotationData.valid_until ?? quotationData.quotation_date,
          freight_value:    Number(quotationData.freight_value) || 0,
          total_value:      Number(quotationData.total_value) || 0,
          notes:            quotationData.notes ?? null,
          payment_conditions:  paymentConditions,
          delivery_conditions: (quotationData as any).delivery_conditions ?? null,
          delivery_deadline:   (quotationData as any).delivery_deadline ?? null,
          customer: {
            name:            customer.name ?? '—',
            document_number: customer.document_number ?? null,
            email:           customer.email ?? null,
            phone:           customer.phone ?? null,
            address:         customer.address ?? null,
            address_number:  customer.address_number ?? null,
            neighborhood:    customer.neighborhood ?? null,
            city:            customer.city ?? null,
            state:           customer.state ?? null,
          },
        },
        company: {
          name:              authCompany?.name ?? '',
          razao_social:      authCompany?.razao_social ?? undefined,
          cnpj:              authCompany?.cnpj ?? undefined,
          endereco:          authCompany?.endereco ?? undefined,
          numero:            authCompany?.numero ?? undefined,
          bairro:            authCompany?.bairro ?? undefined,
          cidade:            authCompany?.cidade ?? undefined,
          estado:            authCompany?.estado ?? undefined,
          cep:               authCompany?.cep ?? undefined,
          telefone:          authCompany?.telefone ?? undefined,
          email_corporativo: authCompany?.email_corporativo ?? undefined,
          logo_url:          authCompany?.logo_url ?? undefined,
        },
        salespersonName,
        groups,
      });
    } catch (error) {
      captureException(error, {
        context: 'ServiceOrderQuotePrintDialog.loadData',
        serviceOrderId,
      });
      toast({
        title: 'Erro',
        description: 'Não foi possível carregar os dados do orçamento.',
        variant: 'destructive',
      });
      setHasError(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (!pdfData) return;
    try {
      setIsGenerating(true);
      const blob = await generateServiceOrderQuotePDF(pdfData);
      const url  = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href     = url;
      link.download = generatePdfFileName(
        customerName,
        'Orcamento',
        quotationNumber ?? 'SN'
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast({ title: 'PDF gerado', description: 'O arquivo foi baixado com sucesso.' });
      onOpenChange(false);
    } catch (error) {
      captureException(error, {
        context: 'ServiceOrderQuotePrintDialog.handleDownloadPDF',
        serviceOrderId,
      });
      toast({
        title: 'Erro',
        description: 'Não foi possível gerar o PDF.',
        variant: 'destructive',
      });
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>
            {isLoading
              ? 'Carregando orçamento...'
              : hasError
              ? 'Orçamento não disponível'
              : quotationNumber
              ? `Baixar Orçamento #${quotationNumber}`
              : 'Imprimir Orçamento'}
          </DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : hasError ? (
          <p className="text-sm text-destructive">
            Esta OS não possui orçamento vinculado ou ocorreu um erro ao carregar.
          </p>
        ) : (
          <p className="text-sm text-muted-foreground">
            O PDF será gerado com os itens agrupados por equipamento.
          </p>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isGenerating}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleDownloadPDF}
            disabled={!pdfData || isGenerating || hasError}
          >
            {isGenerating ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Gerando...
              </>
            ) : (
              <>
                <Download className="mr-2 h-4 w-4" />
                Baixar PDF
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
